import React, { useState, useEffect, useRef } from "react";

const Selector = ({ tabsData, clickHandler, active }) => {
    const [activeTabIndex, setActiveTabIndex] = useState(active);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    
    useEffect(() => {
        setActiveTabIndex(active);
    }, [active]);
    
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    
    const getVisibleTabs = () => {
        const isMobile = window.innerWidth < 640;
        const maxVisible = isMobile ? 3 : 5;
        const tabs = [];
        
        tabs.push(activeTabIndex);
        
        for (let i = 1; i <= Math.floor(maxVisible / 2); i++) {
            if (activeTabIndex - i >= 0 && tabs.length < maxVisible) {
                tabs.unshift(activeTabIndex - i);
            }
        }
        
        for (let i = 1; i <= Math.floor(maxVisible / 2); i++) {
            if (activeTabIndex + i < tabsData.length && tabs.length < maxVisible) {
                tabs.push(activeTabIndex + i);
            }
        }
        
        while (tabs[0] > 0 && tabs.length < maxVisible) {
            tabs.unshift(tabs[0] - 1);
        }
        
        while (tabs[tabs.length - 1] < tabsData.length - 1 && tabs.length < maxVisible) {
            tabs.push(tabs[tabs.length - 1] + 1);
        }
        
        return tabs.sort((a, b) => a - b);
    };
    
    const handleTabClick = (idx) => {
        clickHandler(idx);
        setActiveTabIndex(idx);
    };
    
    const visibleTabs = getVisibleTabs();
    const showFirst = !visibleTabs.includes(0) && activeTabIndex > 1;
    const showLast = !visibleTabs.includes(tabsData.length - 1) && activeTabIndex < tabsData.length - 2;
    const showPrev = activeTabIndex > 0;
    const showNext = activeTabIndex < tabsData.length - 1;
    
    return (
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            {/* Mobile Dropdown */}
            <div className="w-full sm:hidden" ref={dropdownRef}>
                <button
                    className="w-full flex items-center justify-between bg-white/80 backdrop-blur-sm text-gray-700 text-sm px-3 py-2 rounded-lg border border-white/40 hover:bg-white/90 transition-all"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                    <div className="flex items-center gap-2">
                        <span className="font-medium">Part {activeTabIndex + 1}</span>
                        <span className="text-gray-500">/ {tabsData.length}</span>
                    </div>
                    <svg 
                        className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
                
                {isDropdownOpen && (
                    <div className="absolute left-0 right-0 mx-3 mt-1 bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-white/40 py-2 max-h-60 overflow-y-auto z-50">
                        {tabsData.map((_, idx) => (
                            <button
                                key={idx}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50/80 transition-colors flex items-center justify-between ${
                                    idx === activeTabIndex 
                                        ? 'bg-blue-500/10 text-blue-700 font-medium' 
                                        : 'text-gray-700'
                                }`}
                                onClick={() => {
                                    handleTabClick(idx);
                                    setIsDropdownOpen(false);
                                }}
                            >
                                <span>Part {idx + 1}</span>
                                {idx === activeTabIndex && (
                                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            
            {/* Desktop Selector */}
            <div className="hidden sm:flex items-center justify-center space-x-1 bg-white/20 backdrop-blur-sm rounded-xl p-1 border border-white/30 max-w-full overflow-hidden">
                {/* First Button */}
                {showFirst && (
                    <button
                        className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/60 hover:bg-white/80 text-gray-600 border border-white/40 transition-all duration-200 flex-shrink-0 hover:shadow-md"
                        onClick={() => handleTabClick(0)}
                        title="First part"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                        </svg>
                    </button>
                )}
                
                {/* Previous Button */}
                {showPrev && (
                    <button
                        className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/60 hover:bg-white/80 text-gray-600 border border-white/40 transition-all duration-200 flex-shrink-0 hover:shadow-md"
                        onClick={() => handleTabClick(Math.max(0, activeTabIndex - 1))}
                        title="Previous part"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                )}
                
                {/* Ellipsis before current if needed */}
                {visibleTabs[0] > 0 && (
                    <span className="text-white/70 px-1 text-xs">...</span>
                )}
                
                {/* Visible Tabs */}
                <div className="flex items-center space-x-1 overflow-x-auto scrollbar-hide">
                    {visibleTabs.map((idx) => (
                        <button
                            key={idx}
                            className={`min-w-[40px] px-2 py-1 rounded-lg transition-all duration-200 font-medium text-xs flex-shrink-0 border
                                        ${
                                            idx === activeTabIndex
                                            ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg border-blue-400/30 font-semibold"
                                            : "bg-white/60 text-gray-700 hover:text-gray-900 hover:bg-white/80 border-white/40 hover:border-white/60 hover:shadow-md"
                                        }
                                      `}
                            onClick={() => handleTabClick(idx)}
                        >
                            {idx + 1}
                        </button>
                    ))}
                </div>
                
                {/* Ellipsis after current if needed */}
                {visibleTabs[visibleTabs.length - 1] < tabsData.length - 1 && (
                    <span className="text-white/70 px-1 text-xs">...</span>
                )}
                
                {/* Next Button */}
                {showNext && (
                    <button
                        className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/60 hover:bg-white/80 text-gray-600 border border-white/40 transition-all duration-200 flex-shrink-0 hover:shadow-md"
                        onClick={() => handleTabClick(Math.min(tabsData.length - 1, activeTabIndex + 1))}
                        title="Next part"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                )}
                
                {/* Last Button */}
                {showLast && (
                    <button
                        className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/60 hover:bg-white/80 text-gray-600 border border-white/40 transition-all duration-200 flex-shrink-0 hover:shadow-md"
                        onClick={() => handleTabClick(tabsData.length - 1)}
                        title="Last part"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                        </svg>
                    </button>
                )}
            </div>
            
            {/* Quick Jump - Desktop */}
            <div className="hidden sm:flex items-center ml-2">
                <div className="relative">
                    <select
                        className="appearance-none bg-white/80 text-gray-700 text-sm px-3 py-1.5 rounded-lg border border-white/40 focus:outline-none focus:ring-2 focus:ring-blue-300/50 min-w-[120px]"
                        value={activeTabIndex}
                        onChange={(e) => handleTabClick(parseInt(e.target.value))}
                    >
                        {tabsData.map((_, idx) => (
                            <option key={idx} value={idx}>
                                Part {idx + 1}
                            </option>
                        ))}
                    </select>
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Selector;